import { FC, useEffect, useState } from 'react';
import CardContainer from '../../../component/cardContainer/cardContainer';
import styles from './listLayout.module.css';
import { HoverKey, HoverState, ListLayoutProps } from './listLayout.types';
import PlayerIcon from '../../../component/playerIcon/playerIcon';
import { useResponsiveLayout } from '../../../hooks/useResponsiveLayout';
import UpStackedArrow from '../../../assets/exportableIcons/upStackedArrow';
import DownStackedArrow from '../../../assets/exportableIcons/downStackedArrow';
import { Trans } from 'react-i18next';
import { useScrollState } from '../../../hooks/useScrollState';

const ListLayout: FC<ListLayoutProps> = (props: ListLayoutProps) => {
  const { isTablet } = useResponsiveLayout();

  const [hoverState, setHoverState] = useState<HoverState>({
    [HoverKey.HighDown]: false,
    [HoverKey.HighUp]: false,
    [HoverKey.LowDown]: false,
    [HoverKey.LowUp]: false,
  });

  const setHover = (key: HoverKey, value: boolean) => {
    setHoverState((prev) => ({ ...prev, [key]: value }));
  };

  const {
    ref: highRef,
    state: highState,
    scrollToTop: highScrollTop,
    scrollToBottom: highScrollBottom,
  } = useScrollState();

  const {
    ref: lowRef,
    state: lowState,
    scrollToTop: lowScrollTop,
    scrollToBottom: lowScrollBottom,
  } = useScrollState();

  useEffect(() => {}, [
    props.playersData.excluded,
    props.playersData.intervention_required,
    props.playersData.intervention_offered,
    props.playersData.low,
  ]);

  return (
    <div
      className={`${styles.listLayoutWrapper} ${isTablet ? styles.tablet : ''}`}
    >
      <CardContainer className={styles.listLayout}>
        <div className={styles.highRisk}>
          <div className={styles.header}>
            <Trans i18nKey="listView.highRisk" />
          </div>

          <div className={styles.listContainer} ref={highRef}>
            {props.playersData.excluded.length > 0 && (
              <div className={styles.playersWrapper}>
                {props.playersData.excluded.map((player, index) => (
                  <div
                    className={styles.playerWrapper}
                    key={`excluded-${index}`}
                  >
                    <PlayerIcon
                      player={player}
                      onClick={() => props.onPlayerClick(player)}
                    />
                  </div>
                ))}
              </div>
            )}

            {props.playersData.intervention_required.length > 0 && (
              <div className={styles.playersWrapper}>
                {props.playersData.intervention_required.map(
                  (player, index) => (
                    <div
                      className={styles.playerWrapper}
                      key={`required-${index}`}
                    >
                      <PlayerIcon
                        player={player}
                        onClick={() => props.onPlayerClick(player)}
                      />
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <div className={styles.footerText}>
              <Trans i18nKey="listView.displaying" />{' '}
              {props.playersData.intervention_required.length +
                props.playersData.excluded.length +
                '/' +
                (props.playersData.intervention_required.length +
                  props.playersData.excluded.length)}
            </div>

            <div className={styles.scrollIcons}>
              <span
                onMouseEnter={() => setHover(HoverKey.HighDown, true)}
                onMouseLeave={() => setHover(HoverKey.HighDown, false)}
                onClick={() => {
                  if (!highState.scrollable || highState.atBottom) return;
                  highScrollBottom();
                }}
                className={`${styles.cursorPointer} ${
                  !highState.scrollable || highState.atBottom
                    ? 'cursor-not-allowed'
                    : `${styles.cursorActive} cursor-pointer`
                }`}
              >
                <DownStackedArrow
                  active={highState.scrollable && !highState.atBottom}
                  hover={hoverState[HoverKey.HighDown]}
                />
              </span>

              <span
                onMouseEnter={() => setHover(HoverKey.HighUp, true)}
                onMouseLeave={() => setHover(HoverKey.HighUp, false)}
                onClick={() => {
                  if (!highState.scrollable || highState.atTop) return;
                  highScrollTop();
                }}
                className={`${styles.cursorPointer} ${
                  !highState.scrollable || highState.atTop
                    ? 'cursor-not-allowed'
                    : `${styles.cursorActive} cursor-pointer`
                }`}
              >
                <UpStackedArrow
                  active={highState.scrollable && !highState.atTop}
                  hover={hoverState[HoverKey.HighUp]}
                />
              </span>
            </div>
          </div>
        </div>

        <div className={styles.separator}></div>

        <div className={styles.lowRisk}>
          <div className={styles.header}>
            <Trans i18nKey="listView.lowRisk" />
          </div>

          <div className={styles.listContainer} ref={lowRef}>
            {props.playersData.intervention_offered.length > 0 && (
              <div className={styles.playersWrapper}>
                {props.playersData.intervention_offered.map((player, index) => (
                  <div
                    className={styles.playerWrapper}
                    key={`offered-${index}`}
                  >
                    <PlayerIcon
                      player={player}
                      onClick={() => props.onPlayerClick(player)}
                    />
                  </div>
                ))}
              </div>
            )}

            {props.playersData.low.length > 0 && (
              <div className={styles.playersWrapper}>
                {props.playersData.low.map((player, index) => (
                  <div className={styles.playerWrapper} key={`low-${index}`}>
                    <PlayerIcon
                      player={player}
                      onClick={() => props.onPlayerClick(player)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <div className={styles.footerText}>
              <Trans i18nKey="listView.displaying" />{' '}
              {props.playersData.intervention_offered.length +
                props.playersData.low.length +
                '/' +
                (props.playersData.intervention_offered.length +
                  props.playersData.low.length)}
            </div>

            <div className={styles.scrollIcons}>
              <span
                onMouseEnter={() => setHover(HoverKey.LowDown, true)}
                onMouseLeave={() => setHover(HoverKey.LowDown, false)}
                onClick={() => {
                  if (!lowState.scrollable || lowState.atBottom) return;
                  lowScrollBottom();
                }}
                className={`${styles.cursorPointer} ${
                  !lowState.scrollable || lowState.atBottom
                    ? 'cursor-not-allowed'
                    : `${styles.cursorActive} cursor-pointer`
                }`}
              >
                <DownStackedArrow
                  active={lowState.scrollable && !lowState.atBottom}
                  hover={hoverState[HoverKey.LowDown]}
                />
              </span>

              <span
                onMouseEnter={() => setHover(HoverKey.LowUp, true)}
                onMouseLeave={() => setHover(HoverKey.LowUp, false)}
                onClick={() => {
                  if (!lowState.scrollable || lowState.atTop) return;
                  lowScrollTop();
                }}
                className={`${styles.cursorPointer} ${
                  !lowState.scrollable || lowState.atTop
                    ? 'cursor-not-allowed'
                    : `${styles.cursorActive} cursor-pointer`
                }`}
              >
                <UpStackedArrow
                  active={lowState.scrollable && !lowState.atTop}
                  hover={hoverState[HoverKey.LowUp]}
                />
              </span>
            </div>
          </div>
        </div>
      </CardContainer>
    </div>
  );
};

export default ListLayout;
